import React, { useEffect, useState } from "react";
import { Menu, X, Search, Phone } from "lucide-react";
import { company, navLinks } from "../mock";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const Logo = (
    <a href="#home" className="flex items-center gap-2.5 shrink-0">
      <img src={company.logo} alt={company.name} className="h-11 md:h-12 w-auto object-contain" />
      <div className="leading-tight hidden sm:block">
        <div className="font-head font-bold text-[15px] md:text-[17px] tracking-tight" style={{ color: "var(--brand-navy)" }}>Bhawani Kitchen</div>
        <div className="text-[10px] md:text-[11px] tracking-[0.22em] text-slate-500 font-semibold">EQUIPMENT</div>
      </div>
    </a>
  );

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-[0_4px_20px_rgba(10,42,67,0.08)] py-2" : "bg-white/80 backdrop-blur-sm py-3"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-4">
        {Logo}

        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map((l) => (
            <a
              key={l.label}
              href={l.href}
              className="px-3 py-2 text-[14px] font-medium text-slate-700 rounded-md hover:text-[color:var(--brand-blue)] transition-colors relative group"
            >
              {l.label}
              <span className="absolute left-3 right-3 -bottom-0.5 h-0.5 bg-[color:var(--brand-cyan)] scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setSearchOpen((s) => !s)}
            aria-label="Search"
            className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-700 transition-colors"
          >
            {searchOpen ? <X size={20} /> : <Search size={20} />}
          </button>
          <a href="#contact" className="hidden md:inline-flex items-center gap-2 brand-btn px-4 py-2.5 rounded-md text-sm font-semibold">
            <Phone size={16} /> Enquire
          </a>

          <div className="lg:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button aria-label="Menu" className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-800">
                  <Menu size={22} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px]">
                <div className="mt-8 flex flex-col gap-1">
                  {navLinks.map((l) => (
                    <a
                      key={l.label}
                      href={l.href}
                      onClick={() => setMobileOpen(false)}
                      className="px-3 py-3 text-base font-medium text-slate-700 rounded-md hover:bg-slate-100 hover:text-[color:var(--brand-blue)] transition-colors"
                    >
                      {l.label}
                    </a>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Search bar */}
      <div className={`overflow-hidden transition-all duration-300 ${searchOpen ? "max-h-24" : "max-h-0"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-3 pb-1">
          <div className="flex items-center gap-2 border border-slate-200 rounded-lg px-4 py-3 bg-white">
            <Search size={18} className="text-slate-400" />
            <input
              autoFocus={searchOpen}
              placeholder="Search products... (e.g. Visi Cooler, Ice Machine)"
              className="flex-1 outline-none text-sm bg-transparent"
            />
          </div>
        </div>
      </div>
    </header>
  );
}
